import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {uploadDocs} from '../../../modules/admin/docs';
import styles from './styles/upload-modal.module.css';

import ContentBox from './ContentBox';
import TypeBox from './TypeBox';
import UploadBox from './UploadBox';
import UploadList from './UploadList';
import UploadResultModal from './UploadResultModal';
import ButtonBox from '../ButtonBox';

export default function UploadModal({ handleClose, reReadDocsList }) {
    const dispatch = useDispatch();

    const { currentId, duplicateError, duplicateLoading, uploadResponse, uploadError, uploadLoading } = useSelector(({ docs, loading }) => ({
        currentId: docs.currentId,
        duplicateError: docs.duplicateError,
        duplicateLoading: loading['docs/CHECK_DUPLICATED_NAME'],
        uploadResponse: docs.uploadResponse,
        uploadError: docs.uploadError,
        uploadLoading: loading['docs/UPLOAD_DOCS']
    }));

    const [modalType, setModalType] = useState('upload'); // upload or result
    const [uploadFileList, setUploadFileList] = useState([]); // 업로드할 파일 목록
    const [fileType, setFileType] = useState('ORIGINAL');

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));

        return (bytes / Math.pow(1024, i)).toFixed(2).replace(/\.00$/, '') + ' ' + units[i];
    };

    const calculateTotalFileSize = (fileList) => {
        return fileList.reduce((total, file) => {
            const fileSize = file.file ? file.file.size : (file.realSize || 0);
            return total + fileSize;
        }, 0);
    };

    const validateFileList = (newFileList) => { // 파일 추가할 때 기본 검증하는 함수
        const prevFileNameList = uploadFileList.map((prevFile) => { // 기존 파일명 목록 생성 (확장자 제외)
            const fileName = prevFile.name;
            return fileName.substring(0, fileName.lastIndexOf('.'));
        });

        // 파일 총 크기 확인
        const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500MB

        const existingFilesSize = calculateTotalFileSize(uploadFileList);
        
        const newFilesSize = newFileList.reduce((total, file) => total + file.size, 0);

        const totalSize = existingFilesSize + newFilesSize;
    
        const isExceededFileSize = totalSize > MAX_TOTAL_SIZE;

        // 검증된 파일 목록
        const validatedFileList = newFileList.map((newFile) => {
            const uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2, 5); // 고유 id (현재 시간 + 랜덤 문자열)

            const fileData = {
                id: uniqueId,
                file: newFile,
                name: newFile.name,
                realSize: newFile.size,
                size: formatFileSize(newFile.size),
                error: ''
            };
      
            const extension = newFile.name.split('.').pop().toLowerCase(); // 확장자 추출
            const fileName = newFile.name.substring(0, newFile.name.lastIndexOf('.')).trim().normalize('NFC'); // 파일명 추출. normalize('NFC')는 MacOS에서 한글 처리 문제(자모 분리)를 개선하는 메서드.
      
            // 1. 총 파일 크기 검사
            if (isExceededFileSize) {
                fileData.error = '오류 : 총 파일 용량 초과';
                return fileData;
            }

            // 2. 파일 크기 (50MB 초과)
            const maxSize = 50 * 1024 * 1024; // 50MB
            if (newFile.size > maxSize) {
                fileData.error = '오류 : 용량 초과';
                return fileData;
            }

            // 3. 파일명 길이 (2 ~ 100자)
            if (fileName.length < 2 || fileName.length > 100) {
                fileData.error = '오류 : 지원하지 않는 이름 형식';
                return fileData;
            }
      
            // 4. 특수문자
            const specialChars = /[\\/:*?"<>|#%&{}^~]/;
            if (specialChars.test(fileName)) {
                fileData.error = '오류 : 지원하지 않는 이름 형식';
                return fileData;
            }
      
            // 5. 연속된 특수문자
            if (fileName.includes('--') || fileName.includes('__')) {
                fileData.error = '오류 : 지원하지 않는 이름 형식';
                return fileData;
            }
      
            // 6. 확장자
            const allowedExtensions = ['pdf', 'hwp', 'hwpx', 'xls', 'xlsx'];
            if (!allowedExtensions.includes(extension)) {
                fileData.error = '오류 : 지원하지 않는 형식';
                return fileData;
            }
      
            // 7. 중복 파일명 검사
            if (prevFileNameList.includes(fileName)) {
                fileData.error = '오류 : 이미 존재하는 파일명';
                return fileData;
            }
      
            return fileData;
        });
    
        return validatedFileList;
    };

    const validateRemainFileList = (remainedFileList) => { // 파일 제거할 때 기본 검증하는 함수
        const fileNameList = remainedFileList.map((file) => {
            return file.name.substring(0, file.name.lastIndexOf('.')).trim();
        });
        
        return remainedFileList.map((file) => {
            const copiedFile = {...file}; // 복사본
      
            // 이미 다른 오류가 있는 파일은 검사하지 않음
            if (copiedFile.error && copiedFile.error !== '오류 : 이미 존재하는 파일명') {
                return copiedFile;
            }
      
            // 파일명 추출 (확장자 제외)
            const fileName = copiedFile.name.substring(0, copiedFile.name.lastIndexOf('.')).trim();
      
            // 중복 검사 (자기 자신 제외)
            const isDuplicate = fileNameList.filter(name => name === fileName).length > 1;
      
            // 중복 상태에 따라 오류 메시지 설정/제거
            copiedFile.error = isDuplicate ? '오류 : 이미 존재하는 파일명' : '';
      
            return copiedFile;
        });
    };

    const selectFileList = (newFileList) => {
        const newUploadFileList = validateFileList(newFileList);
        
        const updatedFileList = [...uploadFileList, ...newUploadFileList];
        setUploadFileList(updatedFileList);
    };

    const removeFile = (fileId) => {
        const remainedFileList = uploadFileList.filter(file => file.id !== fileId);
        
        const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500MB

        const totalSize = calculateTotalFileSize(remainedFileList);

        const isExceededFileSize = totalSize > MAX_TOTAL_SIZE;
    
        const updatedFileList = remainedFileList.map(file => {
            const newFile = {...file};

            if (newFile.error && newFile.error !== '오류 : 총 파일 용량 초과') { // 이미 다른 오류가 있으면 그대로 유지
                return newFile;
            }

            newFile.error = isExceededFileSize ? '오류 : 총 파일 용량 초과' : '';
            return newFile;
        });
  
        const revalidatedFileList = validateRemainFileList(updatedFileList);
        setUploadFileList(revalidatedFileList);
    };

    const changeFileType = (type) => {
        setFileType(type);
    };

    const validateUploadBtn = () => {
        const isActive = uploadFileList.length > 0 && uploadFileList.every(file => !file.error);
        return isActive;
    };

    const handleUpload = () => {
        const formData = new FormData();

        formData.append('id', currentId); // id 추가
        
        uploadFileList.forEach(fileData => { // 파일 목록 추가
            formData.append('fileList', fileData.file);
        });

        formData.append('fileType', fileType); // 타입 추가

        dispatch(uploadDocs(formData));
    };

    const handleCloseModal = () => {
        setUploadFileList([]);
        handleClose();
    };

    useEffect(() => {
        if (!duplicateLoading && duplicateError && duplicateError.data.nameList.length > 0) {
            const nameList = duplicateError.data.nameList;

            setUploadFileList((prevList) =>
                prevList.map((file) => {
                    if (nameList.includes(file.name)) {
                        return { ...file, error: '오류 : 이미 존재하는 파일명' };
                    }
                    return file;
            }));
        }
    }, [duplicateLoading, duplicateError]);

    useEffect(() => {
        if (!uploadLoading && (uploadResponse || uploadError)) {
            setModalType('result');
        }
    }, [uploadLoading, uploadResponse, uploadError]);

    return (
        <div className={styles.wrapper} datatype={modalType}>
            {modalType === 'upload' &&
                <>
                    <div>
                        <ContentBox
                            title='파일 업로드'
                            content={<UploadBox selectFileList={selectFileList} />}
                        />
                        {uploadFileList.length > 0 &&
                            <ContentBox 
                            title='리스트'
                            content={<UploadList uploadFileList={uploadFileList} removeFile={removeFile} />}
                            />
                        }
                        <ContentBox
                            title='분류'
                            content={<TypeBox fileType={fileType} changeFileType={changeFileType} />}
                        />
                    </div>
                    <ButtonBox 
                        text='업로드'
                        inactiveColor='#CFCED4'
                        activeColor='#825DFC'
                        validateBtn={validateUploadBtn}
                        handleCancle={handleCloseModal}
                        handleClick={handleUpload}
                        loading={uploadLoading}
                    />
                </>
            }

            {modalType === 'result' &&
                <UploadResultModal
                    uploadFileList={uploadFileList}
                    uploadResponse={uploadResponse}
                    uploadError={uploadError}
                    handleUpload={handleUpload}
                    handleCloseModal={handleCloseModal}
                    reReadDocsList={reReadDocsList}
                />
            }
        </div>
    );
}
